"use client"

import { useParams } from "next/navigation"
import type React from "react"
import { useEffect, useState } from "react"
// import { useSetAtom } from "jotai";
// import { refreshSessionAtom } from "@/lib/auth/auth-atom";
import { useTranslation } from "react-i18next"
import ChevronLeftBlack from "@/assets/svg/chevron-left-black.svg"
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	ResponsiveDialogTrigger,
} from "@/components/ui/responsive-dialog"
import { useCartMerge } from "@/hooks/use-cart-merge"
import { useIsMobile } from "@/hooks/use-mobile"
import { authClient } from "@/lib/auth/auth-client"
import { getAreaFromLocale } from "@/lib/i18n/utils"
import { cn } from "@/lib/utils"
import { AdvertisementRegistrationGuide } from "@/modules/cms/advertisement/advertisement-registration-guide"
import {
	FormContainer,
	FormDescription,
	FormMain,
} from "../components/form-items"
import { isEmail, processPhoneInput } from "../utils"
import { ChangeEmailForm } from "./change-email-form"
import { ChangePhoneForm } from "./change-phone-form"
import { CheckForm, CheckFormExtra } from "./check-form"
import { LinkEmailForm } from "./link-email-form"
import { LinkPhoneForm } from "./link-phone-form"
import { LoginFormEmail } from "./login-form-email"
import { LoginFormPhone } from "./login-form-phone"
import { RegisterFormEmail } from "./register-form-email"
import { RegisterFormPhone } from "./register-form-phone"
import { ResetFormEmail } from "./reset-form-email"

// import CfTurnstile from "../components/cf-turnstile";

// Define and export FormState type
export type FormState =
	| "check"
	| "login"
	| "register"
	| "reset"
	| "link-email"
	| "link-phone"
	| "change-email"
	| "change-phone"

interface UserFormModalProps {
	children?: React.ReactNode
	onFinish?: () => void
	initialPageState?: FormState
	initialValue?: string
	open?: boolean
	onOpenChange?: (isOpen: boolean) => void
	modal?: boolean
}

export function UserFormModal({
	children,
	onFinish: onFinishProp,
	initialPageState = "check",
	initialValue = "",
	open: openProp,
	onOpenChange: onOpenChangeProp,
	modal = true,
}: UserFormModalProps) {
	const [pageState, setPageState] = useState<FormState>(initialPageState)
	const [internalOpen, setInternalOpen] = useState(false)
	const { locale } = useParams()
	const isMobile = useIsMobile()
	const [area, setArea] = useState(
		getAreaFromLocale(locale as string)?.toUpperCase(),
	)
	const [inputValue, setInputValue] = useState(initialValue)
	// const setRefreshSession = useSetAtom(refreshSessionAtom);
	const { t } = useTranslation()

	// Cart merge hook
	const { handleLoginSuccess: handleCartMerge, isMerging } = useCartMerge()

	const isControlled = openProp !== undefined
	const currentOpen = isControlled ? openProp : internalOpen

	const { refetch, data: session } = authClient.useSession()

	const handleOpenChange = (isOpen: boolean) => {
		if (isControlled) {
			onOpenChangeProp?.(isOpen)
		} else {
			setInternalOpen(isOpen)
		}
		if (!isOpen) {
			setPageState(initialPageState)
			setInputValue(initialValue)
		}
	}

	useEffect(() => {
		if (session?.user?.email && window?.quickChatApi) {
			window?.quickChatApi?.setContactProperties({
				email: session.user.email,
			})
		}
	}, [session])

	useEffect(() => {
		setPageState(initialPageState)
	}, [initialPageState])

	useEffect(() => {
		setInputValue(initialValue)
	}, [initialValue])

	useEffect(() => {
		if (isControlled) {
			if (!openProp) {
				setPageState(initialPageState)
				setInputValue(initialValue)
			}
		}
	}, [isControlled, openProp, initialPageState, initialValue])

	function _gotoLinkEmail(email?: string) {
		setPageState("link-email")
		if (email) setInputValue(email)
	}

	function _gotoLinkPhone(phone?: string) {
		setPageState("link-phone")
		if (phone) setInputValue(phone)
	}

	function _gotoChangeEmail(email?: string) {
		setPageState("change-email")
		if (email) setInputValue(email)
	}

	function _gotoChangePhone(phone?: string) {
		setPageState("change-phone")
		if (phone) setInputValue(phone)
	}

	function gotoLogin() {
		setPageState("login")
	}

	function gotoRegister() {
		setPageState("register")
	}

	function gotoReset() {
		setPageState("reset")
	}

	async function handleFormCompletion() {
		handleOpenChange(false)

		refetch()

		await handleCartMerge()

		// Call external callback
		onFinishProp?.()
	}

	function handleBack() {
		switch (pageState) {
			case "login":
			case "register":
				setPageState("check")
				break
			case "reset":
				setPageState("login")
				break
			case "link-email":
			case "link-phone":
			case "change-email":
			case "change-phone":
				setPageState("check")
				break
			default:
				setPageState("check")
				break
		}
	}

	const safeInputValue = typeof inputValue === "string" ? inputValue : ""

	const dialogContent = (
		<ResponsiveDialogContent
			onInteractOutside={
				modal && !isMobile ? (e) => e.preventDefault() : undefined
			}
		>
			<ResponsiveDialogHeader className="relative">
				<ResponsiveDialogTitle>
					{(pageState === "login" ||
						pageState === "register" ||
						pageState === "reset") && (
						<ChevronLeftBlack
							className="absolute top-4 left-0 size-5.5 md:size-6"
							onClick={handleBack}
						/>
					)}
					<span>
						{pageState === "check" && t("userForm.title.check")}
						{pageState === "login" && t("userForm.title.login")}
						{pageState === "register" && t("userForm.title.register")}
						{pageState === "reset" && t("userForm.title.resetPassword")}
						{pageState === "link-email" && t("userForm.title.linkEmail")}
						{pageState === "link-phone" && t("userForm.title.linkPhone")}
						{pageState === "change-email" && t("userForm.title.changeEmail")}
						{pageState === "change-phone" && t("userForm.title.changePhone")}
					</span>
				</ResponsiveDialogTitle>
			</ResponsiveDialogHeader>
			<FormContainer className={cn("min-h-[476px] pt-0 pb-0 md:pb-0")}>
				{pageState === "reset" && (
					<FormDescription className=" ">
						{t("userForm.resetPasswordPrompt")}
					</FormDescription>
				)}

				{/* Cart merge status indicator */}
				{isMerging && (
					<div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
						<p className="text-center text-sm text-blue-700">
							{t("cart.mergingCart")}
						</p>
					</div>
				)}

				<FormMain className="mt-6 md:mt-0">
					{pageState === "check" && (
						<CheckForm
							key="check-form-modal"
							area={area}
							setArea={setArea}
							inputValue={safeInputValue}
							setInputValue={setInputValue}
							onUserExists={(data) => {
								// If user exists but doesn't have a password (e.g., phone user who linked email),
								// redirect to reset password form instead of login form
								if (data && data.hasPassword === false) {
									gotoReset()
								} else {
									gotoLogin()
								}
							}}
							onUserNotExists={(data) => {
								if (data && data.hasPassword === false) {
									gotoReset()
								} else {
									gotoRegister()
								}
							}}
						/>
					)}

					{pageState === "login" &&
						(isEmail(safeInputValue) ? (
							<LoginFormEmail
								key="login-email-form-modal"
								email={safeInputValue}
								area={area}
								onResetPassword={gotoReset}
								onFinish={handleFormCompletion}
							/>
						) : (
							<LoginFormPhone
								key="login-phone-form-modal"
								phone={processPhoneInput(safeInputValue, area)}
								area={area}
								onFinish={handleFormCompletion}
							/>
						))}

					{pageState === "register" &&
						(isEmail(safeInputValue) ? (
							<RegisterFormEmail
								key="register-email-form-modal"
								inputValue={safeInputValue}
								area={area}
								onFinish={handleFormCompletion}
							/>
						) : (
							<RegisterFormPhone
								key="register-phone-form-modal"
								phone={processPhoneInput(safeInputValue, area)}
								area={area}
								onFinish={handleFormCompletion}
							/>
						))}

					{pageState === "reset" &&
						(isEmail(safeInputValue) ? (
							<ResetFormEmail
								key="reset-email-form-modal"
								inputValue={safeInputValue}
								area={area}
								onFinish={gotoLogin}
							/>
						) : null)}
					{pageState === "link-email" && (
						<LinkEmailForm
							key="link-email-form-modal"
							area={area}
							onFinish={handleFormCompletion}
						/>
					)}

					{pageState === "link-phone" && (
						<LinkPhoneForm
							key="link-phone-form-modal"
							area={area}
							onFinish={handleFormCompletion}
						/>
					)}

					{pageState === "change-email" && (
						<ChangeEmailForm
							key="change-email-form-modal"
							area={area}
							onFinish={handleFormCompletion}
						/>
					)}

					{pageState === "change-phone" && (
						<ChangePhoneForm
							key="change-phone-form-modal"
							area={area}
							onFinish={handleFormCompletion}
						/>
					)}
				</FormMain>
				{pageState === "check" && (
					<CheckFormExtra
						area={area}
						onSocialLoginSuccess={handleFormCompletion}
					/>
				)}
				{(pageState === "check" ||
					pageState === "login" ||
					pageState === "register") && (
					<AdvertisementRegistrationGuide className="mt-6 md:mt-10" />
				)}
			</FormContainer>
		</ResponsiveDialogContent>
	)

	return (
		<ResponsiveDialog
			open={currentOpen}
			onOpenChange={handleOpenChange}
			modal={modal}
		>
			{!isControlled && children && (
				<ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>
			)}
			{currentOpen && dialogContent}
		</ResponsiveDialog>
	)
}
